/**
 * About CV PDF 공통: Noto 폰트 등록, 다국어 Text, Career 블록 렌더.
 * DocumentVerticalCV / DocumentHorizontalCV에서 공유한다.
 */

import React from 'react';
import { Text as PdfText, View, Font } from '@react-pdf/renderer';
import { CareerPeriodType, CareerType } from '../../shared/enum/EnumRepository';
import {
  CareerData,
  CareerPeriodData,
} from '../../shared/dto/EntityRepository';
import { careerSections, resolveCareerSections } from './CareerData';

const RE = {
  HANGUL: /[\uAC00-\uD7A3]/,
  KANA: /[\u3040-\u30FF\u31F0-\u31FF]/,
  HAN: /[\u3400-\u9FFF\uF900-\uFAFF]/,
};

function pickFont(chunk: string) {
  if (RE.KANA.test(chunk)) return 'NotoJP';
  if (RE.HANGUL.test(chunk)) return 'NotoKR';
  if (RE.HAN.test(chunk)) return 'NotoSC';
  return 'NotoKR';
}

export const CvPdfText = ({ children, style, render, ...rest }: any) => {
  if (typeof render === 'function') {
    return <PdfText style={style} render={render} {...rest} />;
  }

  if (typeof children !== 'string' && typeof children !== 'number') {
    return (
      <PdfText style={style} {...rest}>
        {children}
      </PdfText>
    );
  }

  const s = String(children);

  if (!RE.HANGUL.test(s) && !RE.KANA.test(s) && !RE.HAN.test(s)) {
    return (
      <PdfText style={style} {...rest}>
        {children}
      </PdfText>
    );
  }

  const parts = s
    .split(/([\uAC00-\uD7A3]+|[\u3040-\u30FF\u31F0-\u31FF]+|[\u3400-\u9FFF\uF900-\uFAFF]+)/)
    .filter(Boolean);

  return (
    <PdfText style={style} {...rest}>
      {parts.map((p, i) => (
        <PdfText key={i} style={[style, { fontFamily: pickFont(p) }]}>
          {p}
        </PdfText>
      ))}
    </PdfText>
  );
};

Font.register({
  family: 'NotoKR',
  fonts: [
    { src: '/fonts/NotoSansKR-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansKR-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansKR-SemiBold.ttf', fontWeight: 600 },
  ],
});

Font.register({
  family: 'NotoJP',
  fonts: [
    { src: '/fonts/NotoSansJP-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansJP-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansJP-SemiBold.ttf', fontWeight: 600 },
  ],
});

Font.register({
  family: 'NotoSC',
  fonts: [
    { src: '/fonts/NotoSansSC-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansSC-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansSC-SemiBold.ttf', fontWeight: 600 },
  ],
});

/** StyleSheet.create 결과를 그대로 넘긴다. */
export type CvPdfStyles = {
  Section: Record<string, any>;
  Text: Record<string, any>;
};

type CareerSectionDef = (typeof careerSections)[number];

export function renderCvCareerRow(
  section: CareerSectionDef,
  career: CareerData,
  index: number,
  lineText: string,
  styles: CvPdfStyles,
  getCurrentLabel: (career: CareerPeriodData) => string,
): React.ReactElement {
  const { Section, Text } = styles;
  if (
    career.periodType === CareerPeriodType.PERIOD &&
    (section.displayRange === CareerPeriodType.PERIOD || career.isCurrent)
  ) {
    const endLabel = career.isCurrent
      ? getCurrentLabel(career)
      : career.endYear;

    return (
      <View key={`${section.type}-${career.id}-${index}`} style={Section.careerItem}>
        <CvPdfText style={Text.careerYearStart}>{career.startYear}</CvPdfText>
        <CvPdfText style={Text.separator}>
          {career.startYear && endLabel ? '-' : ''}
        </CvPdfText>
        <CvPdfText style={Text.careerYearEnd}>{endLabel}</CvPdfText>
        <View style={Section.careerContentWrap}>
          {renderCvCareerContent(lineText, Text.careerContent)}
        </View>
      </View>
    );
  }
  return (
    <View key={`${section.type}-${career.id}-${index}`} style={Section.careerItem}>
      <CvPdfText style={Text.careerYear}>{career.startYear}</CvPdfText>
      <View style={Section.careerContentWrap}>
        {renderCvCareerContent(lineText, Text.careerContent)}
      </View>
    </View>
  );
}

/** content의 의도된 줄바꿈(\\n)을 PDF에 반영한다. */
function renderCvCareerContent(lineText: string, style: any): React.ReactNode {
  const lines = lineText.split('\n');
  if (lines.length <= 1) {
    return <CvPdfText style={style}>{lineText}</CvPdfText>;
  }
  return lines.map((line, lineIndex) => (
    <CvPdfText key={`career-line-${lineIndex}`} style={style}>
      {line.length > 0 ? line : ' '}
    </CvPdfText>
  ));
}

export function buildCvCareerBlocks(
  careerList: CareerData[],
  careerText: (c: CareerData) => string,
  styles: CvPdfStyles,
  careerSectionOrder?: readonly CareerType[],
  getCurrentLabel: (career: CareerPeriodData) => string = () => '',
): React.ReactNode[] {
  let firstSectionTitle = true;
  const blocks: React.ReactNode[] = [];
  const { Text } = styles;

  for (const section of resolveCareerSections(careerSectionOrder)) {
    const publicCareers = careerList.filter(
      (c) => c.careerType === section.type && c.isPublic === true
    );
    if (publicCareers.length === 0) continue;
    blocks.push(
      <CvPdfText
        key={`${section.type}-title`}
        style={
          firstSectionTitle
            ? Text.careerTitle
            : [Text.careerTitle, Text.careerTitleSectionGap]
        }
      >
        {section.title}
      </CvPdfText>
    );
    firstSectionTitle = false;
    publicCareers.forEach((career, index) => {
      blocks.push(
        renderCvCareerRow(
          section,
          career,
          index,
          careerText(career),
          styles,
          getCurrentLabel,
        ),
      );
    });
  }
  return blocks;
}
